import React, { useEffect, useState } from "react";
import Sidebar from "../../../component/includes/sidebar";
import { Pencil, Trash2, Plus, Calendar } from "lucide-react";
import { userTaksMessages } from "../../../types/massage";
import {
  CreateTaskData,
  TaskContent,
  taskService,
  UpdateTaskData,
} from "../../../services/Tentang/task";

const DashboardTask = () => {
  const [tasks, setTasks] = useState<TaskContent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [editingTask, setEditingTask] = useState<TaskContent | null>(null);
  const [newTask, setNewTask] = useState<CreateTaskData>({ description: "" });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await taskService.getTasks();
      setTasks(Array.isArray(response) ? response : []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : userTaksMessages.LOAD_FAILED;
      setError(errorMessage);
      setIsErrorModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.description.trim()) {
      setError(userTaksMessages.EMPTY_DESCRIPTION);
      setIsErrorModalOpen(true);
      return;
    }

    try {
      await taskService.createTask(newTask);
      await fetchTasks();
      setSuccessMessage(userTaksMessages.CREATE_SUCCESS);
      setIsSuccessModalOpen(true);
      setNewTask({ description: "" });
      setIsModalOpen(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : userTaksMessages.CREATE_FAILED;
      setError(errorMessage);
      setIsErrorModalOpen(true);
    }
  };

  const handleUpdateTask = async () => {
    if (!editingTask) return;

    try {
      const updatedData: UpdateTaskData = {
        id: editingTask.id,
        description: editingTask.description,
      };
      await taskService.updateTask(updatedData);
      await fetchTasks();
      setSuccessMessage(userTaksMessages.UPDATE_SUCCESS);
      setIsSuccessModalOpen(true);
      setEditingTask(null);
      setIsModalOpen(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : userTaksMessages.UPDATE_FAILED;
      setError(errorMessage);
      setIsErrorModalOpen(true);
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;

    try {
      await taskService.deleteTask(taskId);
      await fetchTasks();
      setSuccessMessage(userTaksMessages.DELETE_SUCCESS);
      setIsSuccessModalOpen(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : userTaksMessages.DELETE_FAILED;
      setError(errorMessage);
      setIsErrorModalOpen(true);
    }
  };

  const truncateDescription = (text: string, maxLength: number = 180) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const filteredTasks = tasks.filter((task) =>
    task.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalTasks = filteredTasks.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar/>

      <div className="flex-1 flex flex-col">
<div className="flex-1 p-4 md:p-6 mt-14">
          <div className="max-w-7xl mx-auto">
            {/* Header - Mirip Gambar */}
            <div className="bg-white rounded-3xl shadow-sm p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center">
                  <span className="text-white text-3xl">📋</span>
                </div>
                <div>
                  <h1 className="text-3xl font-semibold text-gray-900">Task Management</h1>
                  <p className="text-gray-500 text-sm mt-1">Manage your company tasks content</p>
                </div>
              </div>

              <button
                onClick={() => {
                  setIsModalOpen(true);
                  setEditingTask(null);
                  setNewTask({ description: "" });
                }}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-2xl font-medium transition-all active:scale-95"
              >
                <Plus className="w-5 h-5" />
                Add New Task
              </button>
            </div>

            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search task content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-2xl py-4 pl-12 pr-6 focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400"
                />
                <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400">🔍</div>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-20">ID</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">DESCRIPTION</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-40">CREATED AT</th>
                    <th className="px-8 py-5 text-right font-medium text-gray-600 w-32">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredTasks.map((task) => (
                    <tr key={task.id} className="hover:bg-gray-50 transition-colors">
                      {/* ID Circle */}
                      <td className="px-8 py-6">
                        <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-lg">
                          {task.id}
                        </div>
                      </td>

                      {/* Description */}
                      <td className="px-8 py-6">
                        <div className="text-gray-700 leading-relaxed">
                          "{truncateDescription(task.description)}"
                        </div>
                        {task.description.length > 180 && (
                          <button className="text-blue-500 hover:underline text-sm mt-2 flex items-center gap-1">
                            Read more
                          </button>
                        )}
                      </td>

                      {/* Created At */}
                      <td className="px-8 py-6 text-gray-500">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {new Date(task.created_at).toLocaleDateString('id-ID', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-8 py-6">
                        <div className="flex items-center justify-end gap-4">
                          <button
                            onClick={() => {
                              setEditingTask(task);
                              setIsModalOpen(true);
                            }}
                            className="p-3 text-blue-500 hover:bg-blue-50 rounded-2xl transition-all hover:scale-110"
                            title="Edit"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteTask(task.id)}
                            className="p-3 text-red-500 hover:bg-red-50 rounded-2xl transition-all hover:scale-110"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {filteredTasks.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-20 text-center text-gray-400">
                        No tasks found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Total Tasks Footer */}
            <div className="bg-white rounded-3xl shadow-sm mt-6 p-6 text-center">
              <p className="text-gray-600 font-medium">
                Total Tasks: <span className="text-gray-900">{totalTasks}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Create / Edit */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-2xl">
            <h2 className="text-2xl font-semibold mb-6">
              {editingTask ? "Edit Task" : "Add New Task"}
            </h2>
            <textarea
              value={editingTask ? editingTask.description : newTask.description}
              onChange={(e) => {
                if (editingTask) {
                  setEditingTask({ ...editingTask, description: e.target.value });
                } else {
                  setNewTask({ description: e.target.value });
                }
              }}
              placeholder="Enter task description..."
              className="w-full border border-gray-300 rounded-2xl px-6 py-4 h-48 resize-y focus:outline-none focus:border-purple-500"
            />
            <div className="flex gap-4 mt-8">
              <button
                onClick={() => setIsModalOpen(false)}
                className="flex-1 py-4 border border-gray-300 rounded-2xl font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={editingTask ? handleUpdateTask : handleCreateTask}
                className="flex-1 py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-2xl font-medium transition-all"
              >
                {editingTask ? "Update Task" : "Create Task"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {isSuccessModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-green-600 mb-4">Success</h2>
            <p className="text-gray-700 mb-6">{successMessage}</p>
            <button
              onClick={() => setIsSuccessModalOpen(false)}
              className="bg-green-500 text-white px-10 py-3 rounded-2xl hover:bg-green-600"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {isErrorModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
            <p className="text-gray-700 mb-6">{error}</p>
            <button
              onClick={() => setIsErrorModalOpen(false)}
              className="bg-red-500 text-white px-10 py-3 rounded-2xl hover:bg-red-600"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardTask;